/**
 * h5/src/ws/KioskWsClient.ts
 * 生产级 WebSocket 客户端（单例，6 态状态机）
 *
 * 状态机（对齐 E.0 §6.1）：
 * ┌─────────────────────────────────────────────────────────────────┐
 * │ IDLE → CONNECTING → AUTHENTICATING → CONNECTED                  │
 * │              ↑            ↓ authTimeout          ↓ onclose/err  │
 * │         RECONNECTING ◄────────────────────────────              │
 * │              ↓ n>=15                                            │
 * │           FAILED → CONNECTING（手动/navigator.online）           │
 * └─────────────────────────────────────────────────────────────────┘
 *
 * R1: envelope.ts 的 stripDtuFields 在 decode 时自动执行
 * R2: secret 由 SecretProvider 抽象处理
 * R3: 所有日志走 logger.ts
 */

import { ref } from 'vue'
import type { Ref } from 'vue'
import { logger } from '@/utils/logger'
import { decode, encode } from './envelope'
import { createBackoff } from './backoff'
import { createHeartbeat } from './heartbeat'
import { createAckTracker } from './ackTracker'
import { isRecoverableWsAuthFail } from './authFailMessage'
import type {
  WsState,
  KioskWsClientOptions,
  SecretProvider,
  EventBus,
  AnyEventType,
  EventHandler,
  Envelope,
  BackoffOptions,
} from './types'

// ─── 关闭码常量 ──────────────────────────────────────────────────────────────

/** 鉴权失败关闭码（不重连） */
const CLOSE_CODE_AUTH_FAIL = 4001
/** 正常主动断开 */
const CLOSE_CODE_NORMAL = 1000

// ─── KioskWsClient 类 ────────────────────────────────────────────────────────

/**
 * Kiosk WebSocket 客户端
 *
 * 6 态状态机：IDLE / CONNECTING / AUTHENTICATING / CONNECTED / RECONNECTING / FAILED
 *
 * @example
 * ```ts
 * const client = new KioskWsClient({
 *   url: 'wss://kiosk.example.com:8282',
 *   secretProvider: getDefaultProvider(),
 *   bus: createEventBus(),
 * })
 * client.connect()
 * ```
 */
export class KioskWsClient {
  // ── 配置 ──
  private readonly _url: string
  private readonly _secretProvider: SecretProvider
  private readonly _bus: EventBus
  private readonly _heartbeatIntervalMs: number
  private readonly _authTimeoutMs: number
  private readonly _ackTimeoutMs: number
  private readonly _WebSocketFactory: (_url: string) => WebSocket
  private readonly _backoffOpts: BackoffOptions | undefined
  private readonly _appVersion: string

  // ── 状态 ──
  /** 响应式状态（Vue ref，可直接在模板中使用） */
  public readonly state: Ref<WsState>

  // ── 内部组件 ──
  private _ws: WebSocket | null = null
  private _backoff = createBackoff()
  private _heartbeat = createHeartbeat()
  private _ackTracker = createAckTracker()

  // ── 定时器 ──
  private _authTimer: ReturnType<typeof setTimeout> | null = null
  private _reconnectTimer: ReturnType<typeof setTimeout> | null = null

  // ── 标志位 ──
  private _intentionalClose = false   // disconnect() 主动关闭，不触发重连
  private _networkOnlineListener: (() => void) | null = null
  private _networkOfflineListener: (() => void) | null = null

  constructor(opts: KioskWsClientOptions) {
    this._url = opts.url
    this._secretProvider = opts.secretProvider
    this._bus = opts.bus
    this._heartbeatIntervalMs = opts.heartbeatIntervalMs ?? 30_000
    this._authTimeoutMs = opts.authTimeoutMs ?? 10_000
    this._ackTimeoutMs = opts.ackTimeoutMs ?? 10_000
    this._WebSocketFactory = opts.WebSocketFactory ?? ((url) => new WebSocket(url))
    this._backoffOpts = opts.backoffOptions
    this._appVersion = opts.appVersion ?? ''

    this.state = ref<WsState>('IDLE')

    // 重建 backoff（支持注入选项）
    this._backoff = createBackoff(this._backoffOpts)

    // 注册网络事件监听
    this._setupNetworkListeners()
  }

  // ─── 公开 API ──────────────────────────────────────────────────────────────

  /**
   * 发起连接（IDLE → CONNECTING；FAILED → CONNECTING；其他态幂等忽略）
   */
  connect(): void {
    const s = this.state.value
    if (s === 'CONNECTING' || s === 'AUTHENTICATING' || s === 'CONNECTED') {
      logger.info('[WsClient] connect() 调用被忽略（已在连接中）', { state: s })
      return
    }
    // FAILED 态手动重连：重置 backoff
    if (s === 'FAILED') {
      this._backoff.reset()
    }
    this._intentionalClose = false
    this._doConnect()
  }

  /**
   * 主动断开（任何态 → IDLE）
   */
  disconnect(reason = 'user_disconnect'): void {
    logger.info('[WsClient] disconnect() 调用', { reason, state: this.state.value })
    this._intentionalClose = true
    this._cleanup()
    this._setState('IDLE')
  }

  /**
   * 发送信封（可选等待 ACK）
   *
   * @param envelope 已构建的信封（或使用 encode() 构建）
   * @param opts.waitAck 是否等待服务端 ack（默认 false）
   * @returns Promise<void>
   */
  async send(envelope: Envelope, opts?: { waitAck?: boolean }): Promise<void> {
    if (this.state.value !== 'CONNECTED') {
      throw new Error(`[WsClient] send() 失败：当前状态 ${this.state.value} 非 CONNECTED`)
    }
    if (!this._ws) {
      throw new Error('[WsClient] send() 失败：WebSocket 未初始化')
    }

    const raw = JSON.stringify(envelope)
    this._ws.send(raw)
    logger.info('[WsClient] 发送消息', { type: envelope.type, msg_id: envelope.msg_id })

    if (opts?.waitAck) {
      return this._ackTracker.track(envelope.msg_id, this._ackTimeoutMs)
    }
  }

  /**
   * 订阅事件（返回 off 函数）
   *
   * @param type 事件类型（或 '*' 通配符）
   * @param handler 处理函数
   * @returns 取消订阅函数
   */
  on<T = unknown>(type: AnyEventType | '*', handler: EventHandler<T> | ((e: Envelope) => void)): () => void {
    if (type === '*') {
      this._bus.on('*', handler as (e: Envelope) => void)
      return () => this._bus.off('*', handler as (e: Envelope) => void)
    }
    this._bus.on(type as AnyEventType, handler as EventHandler)
    return () => this._bus.off(type as AnyEventType, handler as EventHandler)
  }

  // ─── 内部：连接流程 ─────────────────────────────────────────────────────────

  /**
   * 实际执行连接（申请 ticket → 建立 WebSocket）
   */
  private _doConnect(): void {
    this._setState('CONNECTING')
    logger.info('[WsClient] 开始连接', { url: this._url })

    // 异步申请 ticket
    this._secretProvider
      .buildAuthQuery()
      .then((query) => {
        // 可能在等待 ticket 期间已被 disconnect()
        if (this.state.value !== 'CONNECTING') {
          logger.info('[WsClient] ticket 到达时状态已变更，放弃连接', { state: this.state.value })
          return
        }
        // 附加 app_version 到 URL，后端在 onWebSocketConnect 时更新 sys_machines
        const versionSuffix = this._appVersion ? `&app_version=${encodeURIComponent(this._appVersion)}` : ''
        const fullUrl = `${this._url}?${query}${versionSuffix}`
        this._openWebSocket(fullUrl)
      })
      .catch((err: unknown) => {
        logger.error('[WsClient] 获取 secret 失败，进入 FAILED', { error: String(err) })
        // secret 获取失败 → 直接 FAILED（不重连）
        this._setState('FAILED')
        const payload = { reason: 'secret_fetch_failed', error: String(err) }
        this._bus.emit('error', payload)
        this._bus.emitEnvelope({
          type: 'error',
          payload,
          timestamp: Date.now(),
          msg_id: '',
          ack_required: false,
          server_ts: 0,
        })
      })
  }

  /**
   * 创建 WebSocket 并绑定事件处理器
   */
  private _openWebSocket(url: string): void {
    try {
      this._ws = this._WebSocketFactory(url)
    } catch (err) {
      logger.error('[WsClient] WebSocket 构造失败', { error: String(err) })
      this._scheduleReconnect()
      return
    }

    this._ws.onopen = () => this._onOpen()
    this._ws.onmessage = (ev) => this._onMessage(ev)
    this._ws.onclose = (ev) => this._onClose(ev)
    this._ws.onerror = (ev) => this._onError(ev)
  }

  // ─── 内部：WebSocket 事件处理器 ──────────────────────────────────────────────

  /**
   * WebSocket 握手成功
   * CONNECTING → AUTHENTICATING
   * D 期鉴权在 URL query，onopen 即表示后端已接受连接；
   * 保留 AUTHENTICATING 态等待首条服务端消息或 5s 超时
   */
  private _onOpen(): void {
    logger.info('[WsClient] WebSocket onopen，进入 AUTHENTICATING')
    this._setState('AUTHENTICATING')

    // 启动鉴权超时定时器
    this._authTimer = setTimeout(() => {
      logger.warn('[WsClient] 鉴权超时，触发重连', { timeout_ms: this._authTimeoutMs })
      this._ws?.close()
      this._scheduleReconnect()
    }, this._authTimeoutMs)
  }

  /**
   * 收到服务端消息
   */
  private _onMessage(ev: MessageEvent): void {
    const raw = typeof ev.data === 'string' ? ev.data : String(ev.data)
    const envelope = decode(raw)

    if (!envelope) {
      logger.warn('[WsClient] 收到无效消息帧，忽略', { raw_length: raw.length })
      return
    }

    logger.info('[WsClient] 收到消息', { type: envelope.type, msg_id: envelope.msg_id })

    // ── AUTHENTICATING 态：任意服务端消息（包括 auth_ok / hello / heartbeat_ack）视为认证通过 ──
    if (this.state.value === 'AUTHENTICATING') {
      // 检查是否鉴权失败
      if (envelope.type === 'auth_fail') {
        this._clearAuthTimer()
        const reason = String(
          (envelope.payload as { reason?: unknown } | null)?.reason ?? '',
        )
        // 通配符订阅（pushStore）依赖完整信封；必须先 emitEnvelope
        this._bus.emit('auth_fail', envelope.payload)
        this._bus.emitEnvelope(envelope)

        if (isRecoverableWsAuthFail(reason)) {
          // not_enrolled：持续重连，后台报名完成后可自动恢复（对齐游戏客户端）
          logger.warn('[WsClient] auth_fail 可恢复，关闭后走普通重连', { reason })
          this._ws?.close()
          return
        }

        logger.error('[WsClient] 收到 auth_fail，进入 FAILED（不重连）', { reason })
        this._ws?.close(CLOSE_CODE_AUTH_FAIL)
        this._setState('FAILED')
        return
      }
      // 其他任意消息 → 认证通过
      this._clearAuthTimer()
      this._onAuthenticated()
    }

    // ── 系统消息处理 ──
    switch (envelope.type) {
      case 'heartbeat_ack':
      case 'pong':
        this._heartbeat.notifyAck()
        break

      case 'ack': {
        const ackPayload = envelope.payload as { msg_id?: string }
        if (ackPayload?.msg_id) {
          this._ackTracker.notify(ackPayload.msg_id)
        }
        break
      }

      case 'auth_fail':
        // AUTHENTICATING 已处理并发出 envelope；CONNECTED 后偶发则再广播一次
        if (this.state.value !== 'AUTHENTICATING') {
          this._bus.emit('auth_fail', envelope.payload)
          this._bus.emitEnvelope(envelope)
        }
        return
    }

    // ── ack_required：立即回发 ack ──
    if (envelope.ack_required) {
      this._sendAck(envelope.msg_id)
    }

    // ── 分发到 EventBus ──
    this._bus.emit(envelope.type as AnyEventType, envelope.payload)
    this._bus.emitEnvelope(envelope)
  }

  /**
   * WebSocket 关闭
   */
  private _onClose(ev: CloseEvent): void {
    logger.info('[WsClient] WebSocket onclose', { code: ev.code, reason: ev.reason, clean: ev.wasClean })

    this._clearAuthTimer()
    this._heartbeat.stop()
    this._ackTracker.clear()

    // 主动断开：不重连
    if (this._intentionalClose) {
      logger.info('[WsClient] 主动关闭，不重连')
      return
    }

    // 鉴权失败（4001）：直接 FAILED，不重连
    if (ev.code === CLOSE_CODE_AUTH_FAIL) {
      logger.error('[WsClient] 鉴权失败 (4001)，进入 FAILED')
      this._setState('FAILED')
      this._bus.emit('error', { reason: 'auth_fail', code: ev.code })
      return
    }

    // 其他关闭：触发重连
    this._scheduleReconnect()
  }

  /**
   * WebSocket 错误（通常 onclose 紧随其后）
   */
  private _onError(_event: Event): void {
    logger.warn('[WsClient] WebSocket onerror')
    // 不在此处重连，等 onclose 触发
  }

  // ─── 内部：认证成功 ──────────────────────────────────────────────────────────

  /**
   * AUTHENTICATING → CONNECTED
   */
  private _onAuthenticated(): void {
    logger.info('[WsClient] 认证成功，进入 CONNECTED')
    this._setState('CONNECTED')
    this._backoff.reset()

    // 启动心跳
    this._heartbeat = createHeartbeat({ intervalMs: this._heartbeatIntervalMs })
    this._heartbeat.start(() => {
      if (this._ws && this._ws.readyState === WebSocket.OPEN) {
        const frame = encode('heartbeat', {})
        this._ws.send(frame)

        // 检查心跳存活（前次心跳是否得到响应）
        if (!this._heartbeat.isAlive()) {
          logger.warn('[WsClient] 心跳超时，触发重连')
          this._ws.close()
          this._scheduleReconnect()
        }
      }
    })

    this._bus.emit('auth_ok', {})
  }

  // ─── 内部：重连调度 ──────────────────────────────────────────────────────────

  /**
   * 调度下一次重连
   */
  private _scheduleReconnect(): void {
    // 清理当前 socket
    this._ws = null

    const delay = this._backoff.next()

    if (delay === Infinity) {
      logger.error('[WsClient] 重试次数耗尽，进入 FAILED', { attempts: this._backoff.attempts })
      this._setState('FAILED')
      const payload = { reason: 'max_retries_exceeded', attempts: this._backoff.attempts }
      this._bus.emit('error', payload)
      this._bus.emitEnvelope({
        type: 'error',
        payload,
        timestamp: Date.now(),
        msg_id: '',
        ack_required: false,
        server_ts: 0,
      })
      return
    }

    this._setState('RECONNECTING')
    logger.info('[WsClient] 调度重连', {
      delay_ms: delay,
      attempt: this._backoff.attempts,
    })

    this._reconnectTimer = setTimeout(() => {
      if (!this._intentionalClose && this.state.value === 'RECONNECTING') {
        this._doConnect()
      }
    }, delay)
  }

  // ─── 内部：发送 ack ──────────────────────────────────────────────────────────

  /**
   * 回发 ack 帧（ack_required=true 时）
   */
  private _sendAck(msgId: string): void {
    if (!this._ws || this._ws.readyState !== WebSocket.OPEN) return
    const frame = encode('ack', { msg_id: msgId })
    this._ws.send(frame)
    logger.info('[WsClient] 回发 ack', { msg_id: msgId })
  }

  // ─── 内部：清理 ─────────────────────────────────────────────────────────────

  /**
   * 清理所有资源（timer / heartbeat / ackTracker / socket）
   */
  private _cleanup(): void {
    this._clearAuthTimer()

    if (this._reconnectTimer !== null) {
      clearTimeout(this._reconnectTimer)
      this._reconnectTimer = null
    }

    this._heartbeat.stop()
    this._ackTracker.clear()

    if (this._ws) {
      // 先移除事件处理器，防止 onclose 触发重连
      this._ws.onopen = null
      this._ws.onmessage = null
      this._ws.onclose = null
      this._ws.onerror = null
      if (
        this._ws.readyState === WebSocket.OPEN ||
        this._ws.readyState === WebSocket.CONNECTING
      ) {
        this._ws.close(CLOSE_CODE_NORMAL, 'client_disconnect')
      }
      this._ws = null
    }
  }

  private _clearAuthTimer(): void {
    if (this._authTimer !== null) {
      clearTimeout(this._authTimer)
      this._authTimer = null
    }
  }

  // ─── 内部：状态变更 ──────────────────────────────────────────────────────────

  private _setState(next: WsState): void {
    const prev = this.state.value
    if (prev === next) return
    this.state.value = next
    logger.info('[WsClient] 状态变更', { from: prev, to: next })
    // 将状态变更也通知到 bus（供 pushStore 等使用）
    this._bus.emit('auth_ok', { ws_state: next })
  }

  // ─── 内部：网络事件监听 ──────────────────────────────────────────────────────

  /**
   * 注册 navigator.online / offline 事件
   */
  private _setupNetworkListeners(): void {
    if (typeof window === 'undefined') return

    this._networkOnlineListener = () => {
      logger.info('[WsClient] navigator.online 事件，重置 backoff 并重连')
      if (this.state.value === 'RECONNECTING' || this.state.value === 'FAILED') {
        this._backoff.reset()
        // 清除当前重连定时器
        if (this._reconnectTimer !== null) {
          clearTimeout(this._reconnectTimer)
          this._reconnectTimer = null
        }
        this._doConnect()
      }
    }

    this._networkOfflineListener = () => {
      logger.warn('[WsClient] navigator.offline 事件，主动断开')
      if (this.state.value === 'CONNECTED' || this.state.value === 'AUTHENTICATING') {
        // 不置 intentionalClose，让重连逻辑接管（但 offline 时暂停退避）
        this._ws?.close()
        this._scheduleReconnect()
      }
    }

    window.addEventListener('online', this._networkOnlineListener)
    window.addEventListener('offline', this._networkOfflineListener)
  }

  /**
   * 销毁客户端（移除全局监听器）
   */
  destroy(): void {
    this.disconnect('destroy')
    if (typeof window !== 'undefined') {
      if (this._networkOnlineListener) {
        window.removeEventListener('online', this._networkOnlineListener)
      }
      if (this._networkOfflineListener) {
        window.removeEventListener('offline', this._networkOfflineListener)
      }
    }
    this._bus.clear()
  }
}
